// ============================================
// Vendor Order Detail
// Full order view with status updates and notes
// ============================================

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  AlertCircle,
  User,
  Mail,
  MapPin,
  Truck,
  Package,
  FileText,
  Save,
  CheckCircle2,
} from 'lucide-react';
import { getOrder, updateOrderStatus } from '../../services/firestore/orderService';
import type { Order, OrderStatus } from '../../types/order';

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-text-muted/20 text-text-muted',
  paid: 'bg-gold/15 text-gold',
  processing: 'bg-suboptimal/15 text-suboptimal',
  shipped: 'bg-optimal/15 text-optimal',
  delivered: 'bg-text-secondary/20 text-text-secondary',
  cancelled: 'bg-critical/15 text-critical',
};

export function VendorOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Tracking input state
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');

  // Vendor notes state
  const [vendorNotes, setVendorNotes] = useState('');

  useEffect(() => {
    if (orderId) loadOrder(orderId);
  }, [orderId]);

  async function loadOrder(id: string) {
    try {
      setLoading(true);
      const data = await getOrder(id);
      if (!data) {
        setError('Order not found');
        return;
      }
      setOrder(data);
      setVendorNotes(data.vendorNotes || '');
      setTrackingNumber(data.trackingNumber || '');
      setTrackingCarrier(data.trackingCarrier || '');
      setEstimatedDelivery(data.estimatedDelivery || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusUpdate(newStatus: OrderStatus, extra?: Partial<Order>) {
    if (!order) return;
    try {
      setUpdating(true);
      await updateOrderStatus(order.id, newStatus, extra);
      await loadOrder(order.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  }

  async function handleMarkProcessing() {
    await handleStatusUpdate('processing');
  }

  async function handleMarkShipped() {
    if (!trackingNumber.trim() || !trackingCarrier.trim()) {
      setError('Please enter tracking number and carrier before marking as shipped.');
      return;
    }
    setError(null);
    await handleStatusUpdate('shipped', {
      trackingNumber: trackingNumber.trim(),
      trackingCarrier: trackingCarrier.trim(),
      ...(estimatedDelivery.trim() ? { estimatedDelivery: estimatedDelivery.trim() } : {}),
    });
  }

  async function handleSaveNotes() {
    if (!order) return;
    try {
      setSavingNotes(true);
      setNotesSaved(false);
      await updateOrderStatus(order.id, order.status, { vendorNotes: vendorNotes.trim() });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function formatCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader size={32} className="text-accent-light animate-spin" />
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/vendor/orders')}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors text-base"
        >
          <ArrowLeft size={18} />
          Back to Orders
        </button>
        <div className="flex items-center justify-center py-20 gap-3 text-critical">
          <AlertCircle size={24} />
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back button */}
      <button
        onClick={() => navigate('/vendor/orders')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors text-base"
      >
        <ArrowLeft size={18} />
        Back to Orders
      </button>

      {/* Header */}
      <div
        className="anim-fade-in-up flex flex-wrap items-center gap-4"
      >
        <h2 className="text-2xl font-display text-text-primary">Order Detail</h2>
        <span
          className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
            STATUS_COLORS[order.status]
          }`}
        >
          {order.status}
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="anim-fade-in bg-critical/10 border border-critical/30 rounded-xl p-4 flex items-center gap-3 text-critical"
        >
          <AlertCircle size={20} />
          <p className="text-base">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Info */}
        <div
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-4"
          style={{ animationDelay: '0.05s' }}
        >
          <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <User size={18} className="text-accent-light" />
            Customer Info
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <User size={16} className="text-text-muted mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-text-muted text-sm">Name</p>
                <p className="text-text-primary text-base">{order.customerName || '--'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={16} className="text-text-muted mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-text-muted text-sm">Email</p>
                <p className="text-text-primary text-base">{order.customerEmail}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping Address */}
        <div
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-4"
          style={{ animationDelay: '0.1s' }}
        >
          <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <MapPin size={18} className="text-accent-light" />
            Shipping Address
          </h3>
          {order.shippingAddress ? (
            <div className="text-text-primary text-base leading-relaxed">
              <p>{order.shippingAddress.line1}</p>
              {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
              <p>
                {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
                {order.shippingAddress.postal_code}
              </p>
              <p>{order.shippingAddress.country}</p>
            </div>
          ) : (
            <p className="text-text-muted text-base">No shipping address on file</p>
          )}
        </div>

        {/* Order Details */}
        <div
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-4"
          style={{ animationDelay: '0.15s' }}
        >
          <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <Package size={18} className="text-accent-light" />
            Order Info
          </h3>
          <div className="space-y-3 text-base">
            <div className="flex justify-between">
              <span className="text-text-muted">Order ID</span>
              <span className="text-text-secondary font-mono text-sm">{order.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Bundle</span>
              <span className="text-text-primary">{order.bundleName || '--'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Total</span>
              <span className="text-gold font-semibold">{formatCents(order.totalInCents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Created</span>
              <span className="text-text-secondary">{formatDate(order.createdAt)}</span>
            </div>
            {order.paidAt && (
              <div className="flex justify-between">
                <span className="text-text-muted">Paid</span>
                <span className="text-text-secondary">{formatDate(order.paidAt)}</span>
              </div>
            )}
            {order.shippedAt && (
              <div className="flex justify-between">
                <span className="text-text-muted">Shipped</span>
                <span className="text-text-secondary">{formatDate(order.shippedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Protocol Supplements */}
        <div
          className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-4"
          style={{ animationDelay: '0.2s' }}
        >
          <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
            <FileText size={18} className="text-accent-light" />
            Protocol Supplements
          </h3>
          {order.protocolSnapshot?.supplements && order.protocolSnapshot.supplements.length > 0 ? (
            <ul className="space-y-2">
              {order.protocolSnapshot.supplements.map((supp, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between py-2 border-b border-bg-tertiary/50 last:border-0"
                >
                  <div>
                    <p className="text-text-primary text-base">{supp.name}</p>
                    <p className="text-text-muted text-sm">{supp.dosage} &middot; {supp.timing}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-text-muted text-base">No supplements in protocol</p>
          )}
        </div>
      </div>

      {/* Line Items */}
      <div
        className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-4"
        style={{ animationDelay: '0.25s' }}
      >
        <h3 className="text-lg font-medium text-text-primary">Line Items</h3>
        {order.lineItems.length > 0 ? (
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-bg-tertiary">
                <th className="text-left py-2 text-text-muted font-medium text-sm">Item</th>
                <th className="text-center py-2 text-text-muted font-medium text-sm">Qty</th>
                <th className="text-right py-2 text-text-muted font-medium text-sm">Price</th>
              </tr>
            </thead>
            <tbody>
              {order.lineItems.map((item, i) => (
                <tr key={i} className="border-b border-bg-tertiary/50 last:border-0">
                  <td className="py-2 text-text-primary">{item.name}</td>
                  <td className="py-2 text-center text-text-secondary">{item.quantity}</td>
                  <td className="py-2 text-right text-text-primary">
                    {formatCents(item.priceInCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-text-muted text-base">No line items</p>
        )}
      </div>

      {/* Status Actions */}
      <div
        className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-5"
        style={{ animationDelay: '0.3s' }}
      >
        <h3 className="text-lg font-medium text-text-primary flex items-center gap-2">
          <Truck size={18} className="text-accent-light" />
          Status Actions
        </h3>

        {order.status === 'paid' && (
          <button
            onClick={handleMarkProcessing}
            disabled={updating}
            className="px-6 py-3 rounded-xl bg-suboptimal text-bg-deep font-medium text-base hover:bg-suboptimal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {updating ? <Loader size={18} className="animate-spin" /> : <Package size={18} />}
            Mark Processing
          </button>
        )}

        {order.status === 'processing' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Tracking Number
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="e.g. 1Z999AA10123456784"
                  className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Carrier
                </label>
                <input
                  type="text"
                  value={trackingCarrier}
                  onChange={(e) => setTrackingCarrier(e.target.value)}
                  placeholder="e.g. UPS, FedEx, USPS"
                  className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Estimated Delivery
                </label>
                <input
                  type="date"
                  value={estimatedDelivery}
                  onChange={(e) => setEstimatedDelivery(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors"
                />
              </div>
            </div>
            <button
              onClick={handleMarkShipped}
              disabled={updating}
              className="px-6 py-3 rounded-xl bg-optimal text-bg-deep font-medium text-base hover:bg-optimal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {updating ? <Loader size={18} className="animate-spin" /> : <Truck size={18} />}
              Mark Shipped
            </button>
          </div>
        )}

        {order.status === 'shipped' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-optimal">
                <CheckCircle2 size={18} />
                <span className="text-base font-medium">Shipped</span>
              </div>
              {order.trackingCarrier && (
                <p className="text-text-secondary text-base">
                  Carrier: <span className="text-text-primary">{order.trackingCarrier}</span>
                </p>
              )}
              {order.trackingNumber && (
                <p className="text-text-secondary text-base">
                  Tracking: <span className="text-text-primary font-mono">{order.trackingNumber}</span>
                </p>
              )}
              {order.estimatedDelivery && (
                <p className="text-text-secondary text-base">
                  Est. Delivery: <span className="text-text-primary">{new Date(order.estimatedDelivery).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => handleStatusUpdate('delivered')}
              disabled={updating}
              className="px-6 py-3 rounded-xl bg-text-secondary text-white font-medium text-base hover:bg-text-secondary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {updating ? <Loader size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
              Mark Delivered
            </button>
          </div>
        )}

        {order.status === 'delivered' && (
          <div className="flex items-center gap-2 text-text-secondary">
            <CheckCircle2 size={18} />
            <span className="text-base">Order delivered</span>
            {order.deliveredAt && (
              <span className="text-text-muted text-sm">on {formatDate(order.deliveredAt)}</span>
            )}
          </div>
        )}

        {(order.status === 'pending' || order.status === 'cancelled') && (
          <p className="text-text-muted text-base">
            No actions available for {order.status} orders.
          </p>
        )}
      </div>

      {/* Vendor Notes */}
      <div
        className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 space-y-4"
        style={{ animationDelay: '0.35s' }}
      >
        <h3 className="text-lg font-medium text-text-primary">Vendor Notes</h3>
        <textarea
          value={vendorNotes}
          onChange={(e) => {
            setVendorNotes(e.target.value);
            setNotesSaved(false);
          }}
          placeholder="Add internal notes about this order..."
          rows={4}
          className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors resize-y"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={handleSaveNotes}
            disabled={savingNotes}
            className="px-5 py-2.5 rounded-xl bg-accent-light text-white font-medium text-base hover:bg-accent-light/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {savingNotes ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            Save Notes
          </button>
          {notesSaved && (
            <span
              className="anim-fade-in-left text-optimal text-sm flex items-center gap-1"
            >
              <CheckCircle2 size={14} />
              Saved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
